<template>
  <div>
    <h2>Статусы по ДО</h2>

    <div class="row g-3">

      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="accountingMonth" class="form-label col-md-3">Расчетный месяц</label>
          <div class="col-md-9">
            <div class="input-group">
              <input type="number" min='1' max='12' step="1" class="form-control" id="accountingMonth" v-model="searchParam.accountingMonth">
            </div>
          </div>
        </div>
      </div>

      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="accountingYear" class="form-label col-md-3">Расчетный год</label>
          <div class="col-md-9">
            <div class="input-group">
              <input type="number" min='2024' step="1" class="form-control" id="accountingYear" v-model="searchParam.accountingYear">
            </div>
          </div>
        </div>
      </div>

      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="katoCode" class="form-label col-md-3">Населенный пункт</label>
          <div class="col-md-9">
            <div class="input-group">
              <multiselect
                  v-model="kato"
                  :options="katoList"
                  :searchable="true"
                  :closeOnSelect="true"
                  track-by="code"
                  :custom-label="formattedKatoLabel"
                  placeholder="Выберите Като"
                  :multiple="false"
                  :clear-on-select="true"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="row col-md-7">
        <div class="el-input-group">
          <label for="status" class="form-label col-md-3">Статус финансирования</label>
          <div class="col-md-9">
            <div class="input-group">
              <select class="form-control" id="status" v-model="searchParam.lmsStatus">
                <option value="">Все</option>
                <option value="ACTIVE">Финансируется</option>
                <option value="INACTIVE">Не финансируется</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-7">
        <div class="el-input-group">
          <button v-if="!isLoading" class="btn btn-primary" @click="fetchKindergartenAnalytics()">Поиск</button>
          <button v-if="!isLoading" class="btn btn-info" @click="downloadGeneratedCalculationReportList()">Выгрузить результат по фильтру</button>
          <button v-else class="btn btn-loading" disabled>Загрузка</button>
        </div>
      </div>

    </div>


    <p></p>

    <b-pagination v-model="currentPage" :total-rows="pageResponse?.totalElements" :per-page="searchParam.perPage"></b-pagination>

    <b-table-simple bordered="">
      <b-thead head-variant="light">
        <b-tr>
          <b-th style="width: 3%">#</b-th>
          <b-th style="width: 20%;">Название ДО</b-th>
          <b-th style="width: 10%;">БИН</b-th>
          <b-th style="width: 10%;">LMS ID</b-th>
          <b-th style="width: 17%;">Населённый пункт</b-th>
          <b-th style="width: 7%;">Табель "План" за текущий месяц</b-th>
          <b-th style="width: 7%;">Табель "Факт" за предыдущий месяц</b-th>
          <b-th style="width: 7%;">PDF сформирован</b-th>
          <b-th style="width: 7%;">Подписан</b-th>
          <b-th style="width: 12%;">Финансирование</b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <template v-for="(document, docIndex) in kindergartenAnalytics || []" :key="docIndex">
          <b-tr>
            <b-td>{{ docIndex+1 }}</b-td>
            <b-td>{{document.kindergartenName}}</b-td>
            <b-td>{{document.bin}}</b-td>
            <b-td>{{document.lmsId}}</b-td>
            <b-td>{{document.katoNameRu}}</b-td>
            <b-td>{{getYesNoRu(document.hasPlanTimesheet)}}</b-td>
            <b-td>{{getYesNoRu(document.hasFactTimesheet)}}</b-td>
            <b-td>{{getYesNoRu(document.hasPDFForSign)}}</b-td>
            <b-td>{{getYesNoRu(document.isSigned)}}</b-td>
            <b-td>{{getStatusLabel(document.status)}}</b-td>
          </b-tr>
        </template>
      </b-tbody>
    </b-table-simple>

  </div>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted, reactive, watch} from 'vue';
import {
  downloadReportFile,
  getKindergartenAnalyticsReport
} from '@/services/reportService';
import {PageResponse} from "@/types/pageResponse";
import Multiselect from "vue-multiselect";
import {Kato, KindergartenAnalyticsResponse} from "@/types/kindergarten";
import cacheService from "@/services/cacheService";
import authService from "@/services/authService";
import {getAllKindergartenAnalytics} from "@/services/kindergartenService";
import {SearchParam} from "@/types/searchParam";

export default defineComponent({
  name: 'ReportToSignView',
  components: {Multiselect},
  setup() {
    const pageResponse = ref<PageResponse<KindergartenAnalyticsResponse>>();
    const kindergartenAnalytics = ref<KindergartenAnalyticsResponse[]>([]);
    const currentPage = ref(1);
    const page = ref(1);
    const pageSize = ref(10);
    const accountingMonth = ref(new Date().getMonth() + 1);
    const accountingYear = ref(new Date().getFullYear());
    const kato = ref<Kato | null>(null);
    const katoList = ref<Kato[]>([]);
    const isLoading = ref(false);
    const isFormLoading = ref(false);
    const hasAccess = authService.hasAccess;
    const adminRoles: string[] = authService.getAdminRoles();
    const showDeleteConfirmationModal = ref(false);
    const deleteConfirmationContent = ref({});
    const financialStatus = ref<string>("");
    const searchParam = reactive({
      perPage: 10,
      page: 1,
      lmsStatus: "",
      katoCode: "",
      accountingMonth: new Date().getMonth() + 1,
      accountingYear: new Date().getFullYear(),
    });


    const fetchCachedData = async () => {
      katoList.value = await cacheService.useKatoStore().fetchKato();
    };

    const fetchKindergartenAnalytics = async () => {
      try {
        if(kato.value == null) return null;
        isLoading.value = true;

        const searchParamResponse = new SearchParam(searchParam.perPage, searchParam.page - 1, searchParam, reactive({}));
        if (kato.value) {
          searchParamResponse.filter.katoCode = kato.value?.code;
        }
        pageResponse.value = await getAllKindergartenAnalytics(searchParamResponse);
        kindergartenAnalytics.value = pageResponse.value.content;
        isLoading.value = false;
      } catch (error) {
        console.error('Ошибка при получении отчетов:', error);
      }
    };


    const handleDownload = async (fileId: string, fileName: string) => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          await downloadReportFile(token, fileId, fileName);
        }
      } catch (error) {
        console.error("Ошибка при скачивании файла:", error);
      }
    };

    onMounted(() => {
      fetchKindergartenAnalytics();
      fetchCachedData();
    });

    const formattedKatoLabel = (item: any) => {
      return `${item.code} | ${item.nameRu}`;
    };


    const getKatoNameRu = (katoCode: number) => {
      const kato = katoList.value.find(kato => kato.code === katoCode);
      return kato ? kato.nameRu : "";
    }

    const downloadKindergartenAnalyticsReport = async () => {
      try {
        isLoading.value = true;
        if (kato.value == null) return null;
        const searchParamResponse = new SearchParam(searchParam.perPage, searchParam.page - 1, searchParam, reactive({}));
        if (kato.value) {
          searchParamResponse.filter.katoCode = kato.value?.code;
        }
        await getKindergartenAnalyticsReport(searchParamResponse);
        isLoading.value = false;
      } catch (error) {
        isLoading.value = false;
        console.error(error);
      }
    }

    const getYesNoRu = (data: boolean) => {
      return data ? 'Да' : 'Нет';
    }

    const getStatusLabel = (status: string) => {
      switch (status) {
        case "ACTIVE":
          return "Финансируется";
        case "INACTIVE":
          return "Не финансируется";
        default:
          return status || "Неизвестно";
      }
    }

    watch(currentPage, (newPage) => {
      searchParam.page = newPage;
      fetchKindergartenAnalytics()
    });


    return {
      accountingMonth,
      accountingYear,
      page,
      pageSize,
      currentPage,
      isLoading,
      kato,
      katoList,
      isFormLoading,
      adminRoles,
      showDeleteConfirmationModal,
      deleteConfirmationContent,
      searchParam,
      pageResponse,
      hasAccess,
      fetchKindergartenAnalytics,
      handleDownload,
      formattedKatoLabel,
      getKatoNameRu,
      downloadGeneratedCalculationReportList: downloadKindergartenAnalyticsReport,
      getYesNoRu,
      kindergartenAnalytics,
      financialStatus,
      getStatusLabel
    };
  },
});
</script>

<style scoped>

.rejected-status {
  color: red;
}


.open-modal-btn {
  display: block;
  margin: 20px auto;
  padding: 10px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.open-modal-btn:hover {
  background-color: #45a049;
}

.modal {
  display: block;
  position: fixed;
  z-index: 2;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background-color: #fefefe;
  margin: 15% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 400px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

h4 {
  text-align: center;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-group input {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.submit-btn {
  width: 100%;
  padding: 10px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.submit-btn:hover {
  background-color: #45a049;
}

.close-modal-btn {
  margin-top: 10px;
  width: 100%;
  padding: 10px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.close-modal-btn:hover {
  background-color: #e31b0c;
}

</style>